import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';

import { i18n } from 'app/i18n';
import { AccountSelectButton } from 'app/components/domain/select-button';
import { PendingActionUtils } from 'app/page/orders/utils/pending-action-utils';
import { PendingType } from 'app/page/orders/redux/model/pending-type';
import userIcon from 'app/resources/user.svg';
import { BillingAccount } from 'app/page/orders/model/billing-account';
import { RootState } from 'app/redux/state/root-state';
import { RootActions } from 'app/redux/action/root-actions';
import {
    initializeBillingAccountsActionCreator,
    setSelectedBillingAccountIdActionCreator,
} from 'app/page/orders/redux/orders-action-creators';
import { PendingAction } from 'app/page/orders/redux/model/pending-action';
import { NavigationService, navigationService } from 'app/service/navigation-service';
import { ChoosePage } from 'app/components/domain/choose-page';

interface StateProps {
    initialized: boolean;
    billingAccounts: BillingAccount[];
    selectedBillingAccountId?: string;
    pendingActions: PendingAction[];
}

interface DispatchProps {
    initializeBillingAccounts: typeof initializeBillingAccountsActionCreator;
    setSelectedBillingAccountId: typeof setSelectedBillingAccountIdActionCreator;
}

type Props = StateProps & DispatchProps;

class ChooseAccountPageComponent extends React.Component<Props> {
    public componentDidMount(): void {
        const { initializeBillingAccounts, initialized } = this.props;

        if (!initialized) {
            initializeBillingAccounts();
        }
    }

    private readonly handleSelectBillingAccount = (billingAccount: BillingAccount): void => {
        const { selectedBillingAccountId, setSelectedBillingAccountId } = this.props;

        if (billingAccount.accountId !== selectedBillingAccountId) {
            setSelectedBillingAccountId(billingAccount);
        }

        navigationService.goToPath(NavigationService.CHOOSE_SERVICE_LOCATION);
    };

    public renderAccounts(): React.ReactNode {
        const { billingAccounts, pendingActions } = this.props;

        return billingAccounts.map((account) => (
            <AccountSelectButton
                key={account.accountId}
                loading={PendingActionUtils.isActionWithIdPending(pendingActions, {
                    type: PendingType.LOAD_CUSTOMER_INFORMATION,
                    id: account.accountId,
                })}
                title={account.customerInformation && account.customerInformation.fullName}
                text={`${i18n.chooseAccount.billingAccountId} ${account.accountId}`}
                icon={{
                    src: userIcon,
                    alt: i18n.image.user,
                }}
                onClick={() => this.handleSelectBillingAccount(account)}
            />
        ));
    }

    public render(): React.ReactNode {
        const { initialized } = this.props;

        return (
            initialized && (
                <ChoosePage
                    title={i18n.chooseAccount.title}
                    subtitle={i18n.chooseAccount.whichCustomer}
                    content={this.renderAccounts()}
                />
            )
        );
    }
}

const mapStateToProps = (state: RootState): StateProps => {
    const {
        orders: { initialized, billingAccounts, selectedBillingAccountId, pendingActions },
    } = state;

    return {
        initialized,
        billingAccounts,
        selectedBillingAccountId,
        pendingActions,
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootActions>): DispatchProps => ({
    ...bindActionCreators(
        {
            initializeBillingAccounts: initializeBillingAccountsActionCreator,
            setSelectedBillingAccountId: setSelectedBillingAccountIdActionCreator,
        },
        dispatch,
    ),
});

const ChooseAccountPage = connect(mapStateToProps, mapDispatchToProps)(ChooseAccountPageComponent);

export { ChooseAccountPage };
